import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

interface Recruiter {
  id: string;
  full_name: string;
  email: string;
}

interface PerformanceFiltersProps {
  recruiters: Recruiter[];
  selectedRecruiterId: string | null;
  onRecruiterChange: (id: string | null) => void;
  dateRange: { from: Date | null; to: Date | null };
  onDateRangeChange: (range: { from: Date | null; to: Date | null }) => void;
}

export function PerformanceFilters({
  recruiters,
  selectedRecruiterId,
  onRecruiterChange,
  dateRange,
  onDateRangeChange,
}: PerformanceFiltersProps) {
  const presetRanges = [
    { label: 'Today', days: 0 },
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'Last 90 days', days: 90 },
    { label: 'This week', preset: 'week' },
    { label: 'This month', preset: 'month' },
  ];

  const handlePreset = (preset: { days?: number; preset?: string }) => {
    const now = new Date();
    if (preset.preset === 'week') {
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay());
      startOfWeek.setHours(0, 0, 0, 0);
      onDateRangeChange({ from: startOfWeek, to: now });
    } else if (preset.preset === 'month') {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      onDateRangeChange({ from: startOfMonth, to: now });
    } else if (preset.days !== undefined) {
      const from = new Date();
      from.setDate(from.getDate() - preset.days);
      from.setHours(0, 0, 0, 0);
      onDateRangeChange({ from, to: now });
    }
  };

  return (
    <Card className="p-4">
      <div className="flex flex-wrap gap-4 items-center">
        {/* Recruiter Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Recruiter:</span>
          <Select
            value={selectedRecruiterId || 'all'}
            onValueChange={(value) => onRecruiterChange(value === 'all' ? null : value)}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Recruiters" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Recruiters</SelectItem>
              {recruiters.map((recruiter) => (
                <SelectItem key={recruiter.id} value={recruiter.id}>
                  {recruiter.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date Range */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Period:</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-[240px] justify-start text-left font-normal',
                  !dateRange.from && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateRange.from ? (
                  dateRange.to ? (
                    <>
                      {format(dateRange.from, 'LLL dd, y')} - {format(dateRange.to, 'LLL dd, y')}
                    </>
                  ) : (
                    format(dateRange.from, 'LLL dd, y')
                  )
                ) : (
                  <span>Pick a date range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="p-3 border-b">
                <div className="flex flex-wrap gap-1">
                  {presetRanges.map((preset) => (
                    <Button
                      key={preset.label}
                      variant="outline"
                      size="sm"
                      onClick={() => handlePreset(preset)}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
              </div>
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange.from || undefined}
                selected={{ from: dateRange.from || undefined, to: dateRange.to || undefined }}
                onSelect={(range) =>
                  onDateRangeChange({ from: range?.from || null, to: range?.to || null })
                }
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>
          {dateRange.from && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onDateRangeChange({ from: null, to: null })}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
